import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCoop } from "../contexts/CoopContext";
import { useAuth } from "../contexts/AuthContext";
import { useGame } from "../contexts/GameContext";
import { CLASSES, ENERGY_MAX } from "../data/classes";
import { ELEMENTS } from "../utils/gameLogic";
import { applyPlayerSkill, resolveEnemyTurn, skipStunnedActivePlayer } from "../utils/coopCombatEngine";
import { HealthBar } from "../components/game/StatsDisplay";
import { EnemySprite } from "../components/game/EnemySprite";
import { BreathingSprite } from "../components/game/BreathingSprite";
import { AttackFX } from "../components/game/AttackFX";
import { enemySpriteUrl, enemyFlipToFacePlayer } from "../utils/enemySprites";
import { themeForFloor } from "../utils/themes";
import { Zap, Crown, Swords, LogOut } from "lucide-react";
import { SFX } from "../utils/audio";
import { generateCoopFloor } from "../utils/coopMap";

// Énergie max par classe (Héros = 5, défaut = ENERGY_MAX)
const energyCap = (cid) => CLASSES[cid]?.energyMax || ENERGY_MAX;

// Badges de statut (mêmes mécaniques qu'en solo) — brûlure / gel / stun / buffs.
const CoopStatusBadges = ({ effects, muttons, compact, testId }) => {
  if (!effects && muttons == null) return null;
  const e = effects || {};
  const items = [];
  if (e.brulure?.duration > 0) items.push({ icon: "🔥", label: `${e.brulure.damage}(${e.brulure.duration})`, color: "bg-rose-600" });
  if (e.gel?.duration > 0) items.push({ icon: "❄️", label: `${e.gel.duration}`, color: "bg-cyan-500" });
  if (e.stun?.duration > 0) items.push({ icon: "⭐", label: `${e.stun.duration}`, color: "bg-yellow-500" });
  if (e.posture?.duration > 0) items.push({ icon: "🦅", label: "armée", color: "bg-orange-500" });
  if (e.provoque?.duration > 0) items.push({ icon: "📣", label: `${e.provoque.duration}`, color: "bg-pink-500" });
  const b = e.combatBuffs || {};
  const parts = [];
  if (b.atk) parts.push(`ATK+${b.atk}`);
  if (b.def) parts.push(`DEF+${b.def}`);
  if (b.vit) parts.push(`VIT+${b.vit}`);
  if (b.crit) parts.push(`TC+${b.crit}%`);
  if (b.res) parts.push(`RÉS+${b.res}%`);
  if (parts.length) items.push({ icon: "✨", label: parts.join(" "), color: "bg-purple-500" });
  if (muttons != null && muttons > 0) items.push({ icon: "🐑", label: `${muttons}`, color: "bg-yellow-700" });
  if (items.length === 0) return null;
  return (
    <div data-testid={testId} className="flex flex-wrap gap-0.5 justify-center mt-1 max-w-[140px]">
      {items.map((it, i) => (
        <span key={i} className={`${it.color} text-white border border-slate-950 rounded px-1 ${compact ? "text-[8px]" : "text-[10px]"} font-bold flex items-center gap-0.5`}>
          {it.icon}{it.label}
        </span>
      ))}
    </div>
  );
};

// ============================================================
// CoopCombat — combat d'ÉQUIPE synchronisé (state partagé via WS/CAS).
// - L'ennemi est partagé (PV communs). Les joueurs jouent CHACUN leur tour.
// - L'HÔTE est l'autorité de l'IA ennemie (calcule le tour ennemi).
// - Victoire : ennemi à 0 PV → récompenses à tous. Défaite : tous morts.
// Modèle simplifié (pas de statuts complexes) pour rester robuste en réseau.
// ============================================================

const ENEMY_DELAY = 850;

const CoopCombat = () => {
  const { lobby, publishState, isHost, endRun, leaveLobby } = useCoop();
  const { bumpMeta } = useGame();
  const { user } = useAuth();
  const myId = user?.user_id;
  const navigate = useNavigate();
  const state = lobby?.game_state;
  const rev = lobby?.state_revision;
  const combat = state?.combat;

  const publish = useCallback((producer) => {
    const cur = lobby?.game_state;
    if (!cur) return false;
    const next = producer(structuredClone(cur));
    if (!next) return false;
    return publishState(next, { expectedRevision: rev });
  }, [lobby, rev, publishState]);

  const players = state?.players || {};
  const turnOrder = combat?.turnOrder || [];
  const activeUid = combat?.phase === "player" ? turnOrder[combat.turnIndex] : null;
  const isMyTurn = activeUid === myId;
  const myEnergy = combat?.energy?.[myId] ?? 0;
  const enemy = combat?.enemy;
  const enemyHp = combat?.enemyHp ?? 0;

  const enemyHpRef = useRef(enemyHp);
  useEffect(() => { enemyHpRef.current = enemyHp; }, [enemyHp]);

  // ===== Animations d'attaque 2.5D synchronisées entre clients (via state.combat.fx) =====
  const [enemyAttacking, setEnemyAttacking] = useState(false);
  const [attackingUid, setAttackingUid] = useState(null);
  const [shakeTarget, setShakeTarget] = useState(null); // uid | "enemy"
  const [fxBurst, setFxBurst] = useState(null);          // { id, type, target }
  const [screenShake, setScreenShake] = useState(false);
  const [critFlash, setCritFlash] = useState(false);
  const lastFxTsRef = useRef(0);
  useEffect(() => {
    const fx = combat?.fx;
    if (!fx || !fx.ts || fx.ts === lastFxTsRef.current) return;
    lastFxTsRef.current = fx.ts;
    // Bond de l'attaquant
    if (fx.actor === "enemy") {
      setEnemyAttacking(true);
      setTimeout(() => setEnemyAttacking(false), 380);
    } else {
      setAttackingUid(fx.actor);
      setTimeout(() => setAttackingUid((u) => (u === fx.actor ? null : u)), 380);
    }
    // Effet visuel + recul sur la cible
    const id = fx.ts;
    setFxBurst({ id, type: fx.type, target: fx.target });
    setTimeout(() => setFxBurst((f) => (f && f.id === id ? null : f)), 720);
    if (fx.shake && fx.target) {
      setShakeTarget(fx.target);
      setTimeout(() => setShakeTarget((t) => (t === fx.target ? null : t)), 420);
    }
    // Impact fort (crit / ultime)
    if (fx.crit || fx.ult) {
      setScreenShake(true);
      setTimeout(() => setScreenShake(false), 430);
      setCritFlash(true);
      setTimeout(() => setCritFlash(false), 270);
    }
    // SFX
    if (fx.crit) SFX.crit?.();
    else if (fx.shake) SFX.hit?.();
  }, [combat?.fx]);

  // SFX feedback on phase changes
  const prevPhaseRef = useRef(combat?.phase);
  useEffect(() => {
    if (!combat) return;
    if (prevPhaseRef.current !== combat.phase) {
      if (combat.phase === "victory") SFX.victory?.();
      if (combat.phase === "defeat") SFX.defeat?.();
      prevPhaseRef.current = combat.phase;
    }
  }, [combat]);

  // SFX : montée de tension à la charge, déflagration à la libération
  const prevChargingRef = useRef(false);
  useEffect(() => {
    const isCharging = !!combat?.charging;
    if (isCharging && !prevChargingRef.current) SFX.charge?.();
    if (!isCharging && prevChargingRef.current) {
      const last = (combat?.log || []).slice(-3).join(" ");
      if (last.includes("libère")) SFX.bossRelease?.();
    }
    prevChargingRef.current = isCharging;
  }, [combat]);

  // ===== HOST: drive the enemy turn when phase === 'enemy' =====
  const enemyTimerRef = useRef(null);
  useEffect(() => {
    if (!isHost || !combat || combat.phase !== "enemy") return;
    if (enemyTimerRef.current) return;
    enemyTimerRef.current = setTimeout(() => {
      enemyTimerRef.current = null;
      publish((s) => {
        const c = s.combat;
        if (!c || c.phase !== "enemy") return null;
        resolveEnemyTurn(s);
        return s;
      });
    }, ENEMY_DELAY);
    return () => { if (enemyTimerRef.current) { clearTimeout(enemyTimerRef.current); enemyTimerRef.current = null; } };
  }, [isHost, combat, publish]);

  // ===== HOST: un joueur actif etourdi/gele passe automatiquement son tour =====
  const skipTimerRef = useRef(null);
  useEffect(() => {
    if (!isHost || !combat || combat.phase !== "player") return;
    if (skipTimerRef.current) return;
    skipTimerRef.current = setTimeout(() => {
      skipTimerRef.current = null;
      publish((s) => {
        const c = s.combat;
        if (!c || c.phase !== "player") return null;
        return skipStunnedActivePlayer(s) ? s : null;
      });
    }, 700);
    return () => { if (skipTimerRef.current) { clearTimeout(skipTimerRef.current); skipTimerRef.current = null; } };
  }, [isHost, combat, publish]);

  // ===== Player action: cast a skill (tous les types, comme en solo) =====
  const castSkill = useCallback((comp) => {
    if (!isMyTurn) return;
    if (myEnergy < (comp.cout || 0)) { SFX.miss?.(); return; }
    if (comp.requireMuttons && (combat?.muttons?.[myId] ?? 0) < comp.requireMuttons) { SFX.miss?.(); return; }
    const myPe = combat?.playerEffects?.[myId];
    if ((myPe?.stun?.duration || 0) > 0 || (myPe?.gel?.duration || 0) > 0) { SFX.miss?.(); return; }
    SFX.click?.();
    publish((s) => {
      const c = s.combat;
      if (!c || c.phase !== "player" || c.turnOrder[c.turnIndex] !== myId) return null;
      applyPlayerSkill(s, myId, comp);
      return s;
    });
  }, [isMyTurn, myEnergy, combat, myId, publish]);

  const defend = useCallback(() => {
    if (!isMyTurn) return;
    SFX.click?.();
    publish((s) => {
      const c = s.combat;
      if (!c || c.phase !== "player" || c.turnOrder[c.turnIndex] !== myId) return null;
      c.energy[myId] = Math.min(energyCap(s.players[myId]?.classe_id), (c.energy[myId] ?? 0) + 2);
      if (c.charging) {
        c.charging.blocked = true;
        c.log = [...(c.log || []), `🛡️ ${s.players[myId].name} se prépare à BLOQUER la charge (+2 énergie).`].slice(-8);
      } else {
        c.log = [...(c.log || []), `🛡️ ${s.players[myId].name} se prépare (+2 énergie).`].slice(-8);
      }
      const next = c.turnIndex + 1;
      if (next >= c.turnOrder.length) c.phase = "enemy";
      else c.turnIndex = next;
      return s;
    });
  }, [isMyTurn, myId, publish]);

  // ===== HOST: resolve victory cleanup (rewards, clear combat, advance floor) =====
  const victoryTimerRef = useRef(null);
  useEffect(() => {
    if (!isHost || !combat) return;
    if (combat.phase !== "victory") return;
    if (victoryTimerRef.current) return;
    victoryTimerRef.current = setTimeout(() => {
      victoryTimerRef.current = null;
      publish((s) => {
        const c = s.combat;
        if (!c || c.phase !== "victory") return null;
        const gold = c.enemy.goldReward || 10;
        const isBoss = !!c.enemy.isBoss;
        // Reward alive players & restore energy a bit
        for (const uid of Object.keys(s.players)) {
          const pl = s.players[uid];
          if (pl.alive && (pl.character?.stats?.pv || 0) > 0) {
            const rs = { ...(pl.character.runStats || {}) };
            rs.goldEarned = (rs.goldEarned || 0) + gold;
            pl.character = { ...pl.character, gold: (pl.character.gold || 0) + gold, runStats: rs };
          }
        }
        // Mark the room cleared globally
        const rk = c.roomKey;
        if (rk && s.grid?.rooms?.[rk]) s.grid.rooms[rk].combatCleared = true;
        s.combat = null;
        // Boss → advance floor for everyone (fresh open floor, heal alive players 40%)
        if (isBoss) {
          const newFloor = (s.floor || 1) + 1;
          s.floor = newFloor;
          const fresh = generateCoopFloor(newFloor);
          s.grid = { rooms: fresh.rooms, startKey: fresh.startKey, bossKey: fresh.bossKey };
          for (const uid of Object.keys(s.players)) {
            const pl = s.players[uid];
            pl.roomKey = fresh.startKey;
            if (pl.alive && pl.character) {
              const st = { ...pl.character.stats };
              st.pv = Math.min(st.pvMax, st.pv + Math.round(st.pvMax * 0.4));
              pl.character = { ...pl.character, stats: st };
            }
          }
        }
        return s;
      });
    }, 2600);
    return () => { if (victoryTimerRef.current) { clearTimeout(victoryTimerRef.current); victoryTimerRef.current = null; } };
  }, [isHost, combat, publish]);

  const aliveCount = useMemo(() => Object.values(players).filter((p) => p.alive && (p.character?.stats?.pv || 0) > 0).length, [players]);

  // ===== HOST: on total party kill, end the run after showing the defeat screen =====
  const defeatTimerRef = useRef(null);
  useEffect(() => {
    if (!isHost || !combat || combat.phase !== "defeat") return;
    if (defeatTimerRef.current) return;
    defeatTimerRef.current = setTimeout(() => { defeatTimerRef.current = null; endRun(); }, 3500);
    return () => { if (defeatTimerRef.current) { clearTimeout(defeatTimerRef.current); defeatTimerRef.current = null; } };
  }, [isHost, combat, endRun]);

  // ===== Succès "Esprit d'équipe" : finir 3 parties coop (compté pour chaque joueur à la défaite) =====
  const teamPlayerCountedRef = useRef(false);
  useEffect(() => {
    if (combat?.phase === "defeat" && !teamPlayerCountedRef.current) {
      teamPlayerCountedRef.current = true;
      bumpMeta?.("coopGamesFinished", 1, { 3: "team_player" });
    }
  }, [combat, bumpMeta]);

  if (!combat || !enemy) return null;

  const myClass = CLASSES[players[myId]?.classe_id];
  // Toutes les compétences de la classe sont disponibles en coop (parité solo).
  const competences = myClass?.competences || [];
  const myEffects = combat?.playerEffects?.[myId] || null;
  const myMuttonsCount = combat?.muttons?.[myId];

  // Décor du biome + disposition "arène" (équipe à gauche en quinconce, ennemi à droite)
  const theme = themeForFloor(state?.floor || 1);
  const enemySize = enemy.isBoss
    ? "w-40 h-40 sm:w-52 sm:h-52"
    : enemy.isElite
      ? "w-32 h-32 sm:w-44 sm:h-44"
      : "w-28 h-28 sm:w-36 sm:h-36";
  const playerList = turnOrder.concat(Object.keys(players).filter((u) => !turnOrder.includes(u)));
  // Formation en quinconce : alternance de profondeur, avancée vers l'ennemi
  const FORMATION = [
    "bottom-[8%] left-[4%] sm:left-[7%]",
    "bottom-[26%] left-[11%] sm:left-[14%]",
    "bottom-[11%] left-[19%] sm:left-[22%]",
    "bottom-[30%] left-[26%] sm:left-[29%]",
  ];

  return (
    <div data-testid="coop-combat" className="absolute inset-0 z-40 bg-slate-950 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="px-4 py-2 border-b-4 border-slate-950 bg-slate-800 flex items-center justify-between">
        <span className="font-display font-bold text-sm uppercase tracking-wider text-fuchsia-300 flex items-center gap-1">
          <Crown className="w-4 h-4 text-yellow-400" /> Combat d'équipe
        </span>
        <span data-testid="coop-turn-indicator" className="text-sm font-bold">
          {combat.phase === "victory" ? <span className="text-emerald-400">VICTOIRE !</span>
            : combat.phase === "defeat" ? <span className="text-rose-400">DÉFAITE…</span>
            : combat.phase === "enemy" ? <span className="text-rose-400">Tour de l'ennemi…</span>
            : isMyTurn ? <span className="text-emerald-400">À TON tour</span>
            : <span className="text-amber-400">Tour de {players[activeUid]?.name || "…"}</span>}
        </span>
        <button data-testid="coop-combat-leave-btn"
          onClick={() => { if (window.confirm("Quitter la partie coop ? Tu abandonneras ce combat.")) { SFX.click?.(); leaveLobby().then(() => navigate("/")); } }}
          className="bg-rose-500 hover:bg-rose-400 text-white border-2 border-slate-950 rounded-lg px-2.5 py-1 text-xs font-bold flex items-center gap-1 transition-colors">
          <LogOut className="w-3 h-3" /> Quitter
        </button>
      </div>

      {/* Bonus d'équipe actifs (Renforcement) */}
      {(combat.allyBuffs || []).length > 0 && (
        <div data-testid="coop-team-buffs" className="flex justify-center flex-wrap gap-1 py-1 bg-slate-900/60 border-b-2 border-slate-800">
          {combat.allyBuffs.map((b, i) => (
            <span key={i} className="bg-emerald-600 text-white border-2 border-slate-950 rounded-md px-2 py-0.5 text-[10px] font-bold">
              🛡️ Équipe +{b.value}{b.stat === "res" ? "%" : ""} {(b.stat || "").toUpperCase()} ({b.duration})
            </span>
          ))}
        </div>
      )}

      {/* Boss charge banner */}
      {combat.charging && (
        <div data-testid="coop-boss-charge-banner" className="absolute top-14 left-1/2 -translate-x-1/2 z-50 w-[92%] max-w-lg pointer-events-none animate-pulse">
          <div className="bg-gradient-to-r from-rose-700 via-red-600 to-rose-700 border-4 border-yellow-300 rounded-2xl px-4 py-2.5 text-center shadow-2xl">
            <p className="font-display text-base sm:text-lg font-bold text-yellow-200 uppercase tracking-wide" style={{ textShadow: "2px 2px 0 #000" }}>
              ⚠️ {enemy.nom} CHARGE — {combat.charging.sigNom}
            </p>
            <p className="text-xs text-white font-bold mt-0.5">
              Infligez <span className="text-yellow-300">{combat.charging.threshold} dégâts</span> pour INTERROMPRE · ou <span className="text-sky-300">Défendez</span> (−80%)
            </p>
          </div>
        </div>
      )}

      {/* ===== Arène (décor du biome) ===== */}
      <div data-testid="coop-arena" className={`relative flex-1 overflow-hidden ${screenShake ? "animate-screen-shake" : ""}`}
        style={{ backgroundImage: `url(/arenas/${theme.id}.png)`, backgroundSize: "cover", backgroundPosition: "center" }}>
        {critFlash && <div data-testid="coop-crit-flash" className="absolute inset-0 z-40 bg-white/30 pointer-events-none animate-crit-flash" />}
        <div className="absolute inset-0 z-0 pointer-events-none shadow-[inset_0_0_120px_rgba(0,0,0,0.85)] bg-gradient-to-t from-slate-950/85 via-slate-950/10 to-slate-950/40" />

        {/* Journal de combat (haut-gauche) */}
        <div data-testid="coop-combat-log" className="absolute top-2 left-2 z-30 w-48 sm:w-72 bg-slate-900/85 border-4 border-slate-950 rounded-xl px-3 py-2 text-[11px] sm:text-sm text-slate-100 shadow-[4px_4px_0_0_#020617] backdrop-blur-sm flex flex-col justify-end gap-1 pointer-events-none">
          {(combat.log || []).slice(-4).map((l, i, arr) => (
            <p key={i} className={`leading-snug break-words ${i === arr.length - 1 ? "text-white font-semibold" : "text-slate-300"}`}>{l}</p>
          ))}
        </div>

        {/* Ennemi (droite, face à l'équipe) */}
        <div className={`absolute z-10 bottom-[26%] right-[5%] sm:right-[10%] flex flex-col items-center ${shakeTarget === "enemy" ? "animate-hit-enemy" : ""}`}>
          <h3 className="font-display text-sm sm:text-lg font-bold drop-shadow-[2px_2px_0_#000] text-center">
            {enemy.nom}
            {enemy.isBoss && <span className="ml-1 bg-purple-600 text-white border-2 border-slate-950 rounded px-1.5 py-0.5 text-[10px] uppercase">Boss</span>}
            {enemy.isElite && <span className="ml-1 bg-rose-700 text-white border-2 border-slate-950 rounded px-1.5 py-0.5 text-[10px] uppercase">Élite</span>}
          </h3>
          {enemy.element && ELEMENTS[enemy.element] && (
            <span className={`${ELEMENTS[enemy.element].couleur} ${ELEMENTS[enemy.element].textColor} border-2 border-slate-950 rounded-md px-2 py-0.5 text-[10px] font-bold mt-0.5`}>
              {ELEMENTS[enemy.element].icon} {ELEMENTS[enemy.element].nom}
            </span>
          )}
          <div className="w-32 sm:w-40 mt-1">
            <HealthBar current={enemyHp} max={enemy.stats.pvMax} testId="coop-enemy-hp" showText={false} colorByHealth className="w-full" />
          </div>
          <CoopStatusBadges effects={combat.enemyEffects} testId="coop-enemy-status" />
          <div className={`relative mt-1 ${enemyAttacking ? "animate-lunge-enemy" : ""}`}>
            {combat.charging && (
              <div data-testid="coop-boss-cast-ring" className="absolute -inset-3 z-20 pointer-events-none flex items-center justify-center">
                <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                  <circle cx="50" cy="50" r="46" fill="none" stroke="rgba(253,224,71,0.22)" strokeWidth="6" />
                  <circle cx="50" cy="50" r="46" fill="none" stroke="#fbbf24" strokeWidth="6" strokeLinecap="round" strokeDasharray="289" strokeDashoffset="289">
                    <animate attributeName="stroke-dashoffset" from="289" to="0" dur="1.3s" repeatCount="indefinite" />
                  </circle>
                </svg>
                <div className="absolute inset-1 rounded-full bg-rose-500/20 animate-ping" />
              </div>
            )}
            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-24 sm:w-32 h-5 bg-black/45 rounded-[100%] blur-[3px] z-0" />
            <div className={`${enemySize} relative flex items-center justify-center ${enemyHp > 0 ? "" : "opacity-30 grayscale"} ${shakeTarget === "enemy" ? "animate-hit-flash" : ""}`}>
              {enemySpriteUrl(enemy.id)
                ? <EnemySprite enemyId={enemy.id} alt={enemy.nom} facingLeft={enemyFlipToFacePlayer(enemy.id)} className="w-full h-full object-contain drop-shadow-[0_10px_8px_rgba(0,0,0,0.5)]" testId="coop-enemy-sprite" />
                : <span className="text-7xl drop-shadow-[0_6px_6px_rgba(0,0,0,0.5)]">{enemy.emoji}</span>}
              {fxBurst?.target === "enemy" && <AttackFX key={fxBurst.id} type={fxBurst.type} testId="coop-enemy-attack-fx" />}
            </div>
          </div>
        </div>

        {/* Équipe (gauche, en quinconce, face à l'ennemi) */}
        {playerList.map((uid, idx) => {
          const pl = players[uid];
          if (!pl) return null;
          const cls = CLASSES[pl.classe_id];
          const dead = !pl.alive || (pl.character?.stats?.pv || 0) <= 0;
          const isActive = uid === activeUid;
          const slot = FORMATION[idx] || FORMATION[FORMATION.length - 1];
          return (
            <div key={uid} data-testid={`coop-fighter-${uid}`} className={`absolute z-20 ${slot} flex flex-col items-center ${shakeTarget === uid ? "animate-hit-player" : ""}`}>
              <div className={`text-[10px] sm:text-xs font-bold px-1.5 rounded drop-shadow-[1px_1px_0_#000] ${uid === myId ? "text-fuchsia-300" : "text-white"}`}>
                {pl.name}{uid === myId ? " (toi)" : ""}
              </div>
              <div className="w-16 sm:w-20">
                <HealthBar current={Math.max(0, pl.character?.stats?.pv || 0)} max={pl.character?.stats?.pvMax || 1} testId={`coop-hp-${uid}`} showText={false} colorByHealth className="w-full" />
              </div>
              <div className="flex items-center gap-0.5 text-[9px] text-sky-300 font-bold drop-shadow-[1px_1px_0_#000]">
                <Zap className="w-2.5 h-2.5" /> {combat.energy?.[uid] ?? 0}/{cls?.energyMax || ENERGY_MAX}
              </div>
              <CoopStatusBadges effects={combat.playerEffects?.[uid]} muttons={cls?.hasMuttons ? combat.muttons?.[uid] : null} compact testId={`coop-status-${uid}`} />
              <div className={`relative ${attackingUid === uid ? "animate-lunge-player" : ""}`}>
                <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-16 sm:w-20 h-4 bg-black/45 rounded-[100%] blur-[3px] z-0" />
                {isActive && !dead && (
                  <div className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-16 sm:w-20 h-5 bg-emerald-400/40 rounded-[100%] blur-[2px] border-2 border-emerald-300/70 animate-pulse" />
                )}
                {uid === myId && !dead && !isActive && (
                  <div className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-16 sm:w-20 h-5 bg-fuchsia-400/25 rounded-[100%] blur-[2px] border-2 border-fuchsia-300/50" />
                )}
                <div className={`w-20 h-20 sm:w-28 sm:h-28 relative ${dead ? "opacity-40 grayscale" : ""} ${shakeTarget === uid ? "animate-hit-flash" : ""}`}>
                  {cls && <BreathingSprite src={`/sprites/side/${pl.classe_id}.png`} alt={cls.nom} testId={`coop-sprite-${uid}`} className="w-full h-full drop-shadow-[0_8px_6px_rgba(0,0,0,0.5)]" />}
                  {dead && <span className="absolute inset-0 flex items-center justify-center text-3xl">☠</span>}
                  {fxBurst?.target === uid && <AttackFX key={fxBurst.id} type={fxBurst.type} testId={`coop-player-attack-fx-${uid}`} />}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Skill bar */}
      <div className="border-t-4 border-slate-950 bg-slate-800 p-3">
        {combat.phase === "victory" && (
          <div className="text-center text-emerald-400 font-display font-bold py-2">Ennemi vaincu ! +{enemy.goldReward || 10} or chacun 🪙</div>
        )}
        {combat.phase === "defeat" && (
          <div className="text-center text-rose-400 font-display font-bold py-2">Toute l'équipe est tombée…</div>
        )}
        {(combat.phase === "player" || combat.phase === "enemy") && (
          <div className="flex flex-wrap gap-2 justify-center max-w-5xl mx-auto">
            {competences.map((comp) => {
              const stunnedMe = (myEffects?.stun?.duration || 0) > 0 || (myEffects?.gel?.duration || 0) > 0;
              const muttonOk = !comp.requireMuttons || (myMuttonsCount ?? 0) >= comp.requireMuttons;
              const canCast = isMyTurn && !stunnedMe && muttonOk && myEnergy >= (comp.cout || 0) && combat.phase === "player";
              return (
                <button key={comp.id}
                  data-testid={`coop-skill-${comp.id}`}
                  onClick={() => castSkill(comp)}
                  disabled={!canCast}
                  title={comp.description}
                  className={`bg-yellow-400 text-slate-950 font-display font-bold py-2 px-3 rounded-2xl border-4 border-slate-950 cartoon-shadow disabled:opacity-40 disabled:cursor-not-allowed flex flex-col items-center gap-0.5 min-w-[96px]`}>
                  <span className="text-lg">{comp.icon}</span>
                  <span className="text-xs leading-tight text-center">{comp.nom}</span>
                  <span className="text-[10px] opacity-80">{comp.cout === 0 ? "Gratuit" : `${comp.cout}⚡`}{comp.ultimate ? " · ULT" : ""}{comp.requireMuttons ? " · 🐑" : ""}</span>
                </button>
              );
            })}
            <button data-testid="coop-defend-btn" onClick={defend} disabled={!isMyTurn || combat.phase !== "player"}
              className="bg-blue-600 text-white font-display font-bold py-2 px-3 rounded-2xl border-4 border-slate-950 cartoon-shadow disabled:opacity-40 flex flex-col items-center gap-0.5 min-w-[96px]">
              <Swords className="w-5 h-5" />
              <span className="text-xs">Défendre</span>
              <span className="text-[10px] opacity-80">+2⚡</span>
            </button>
          </div>
        )}
        {!isMyTurn && combat.phase === "player" && (
          <p className="text-center text-amber-300 text-xs mt-2">En attente du tour de {players[activeUid]?.name}… ({aliveCount} en vie)</p>
        )}
        {isMyTurn && combat.phase === "player" && ((myEffects?.stun?.duration || 0) > 0 || (myEffects?.gel?.duration || 0) > 0) && (
          <p className="text-center text-rose-300 text-xs mt-2">{(myEffects?.gel?.duration || 0) > 0 ? "❄️ Tu es gelé" : "⭐ Tu es étourdi"} — ton tour est passé automatiquement.</p>
        )}
      </div>
    </div>
  );
};

export default CoopCombat;

